// src/components/LearningSuggestionsPage.js — مركز التعلم الذاتي
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box, Card, Typography, Button, Chip, Alert, CircularProgress, Stack, Divider,
} from '@mui/material';
import PageHeader from './common/PageHeader';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import learningService from '../api/learningService';

const T = { ink: '#0f172a', muted: '#64748b', faint: '#94a3b8', border: '#e9eef5', brand: '#0ea5e9' };
const PAGE = 15;

// suggested_value is stored as "Q: <customer question>\nA: <staff answer>"
const parseQA = (raw = '') => {
  const aIdx = raw.indexOf('\nA:');
  if (raw.startsWith('Q:') && aIdx !== -1) {
    return { q: raw.slice(2, aIdx).trim(), a: raw.slice(aIdx + 3).trim() };
  }
  return { q: '', a: raw.trim() };
};

const ChatLine = ({ who, text, icon, bg, fg, align }) => (
  <Box sx={{ display: 'flex', gap: 1.25, flexDirection: align === 'end' ? 'row-reverse' : 'row', mb: 1 }}>
    <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: bg, color: fg, display: 'grid', placeItems: 'center', flexShrink: 0, mt: 0.25 }}>
      {icon}
    </Box>
    <Box sx={{ maxWidth: '82%' }}>
      <Typography sx={{ fontSize: '0.68rem', color: T.faint, fontWeight: 700, mb: 0.25, textAlign: align === 'end' ? 'left' : 'right' }}>{who}</Typography>
      <Box sx={{
        px: 1.75, py: 1.15, borderRadius: 2.5, bgcolor: bg, color: T.ink,
        fontSize: '0.88rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', border: `1px solid ${bg === '#ffffff' ? T.border : 'transparent'}`,
      }}>
        {text || '—'}
      </Box>
    </Box>
  </Box>
);

export default function LearningSuggestionsPage() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [visible, setVisible] = useState(PAGE);

  const fetch = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await learningService.getSuggestions();
      setSuggestions((Array.isArray(data) ? data : data.results || []).filter((s) => s.status === 'PENDING'));
    } catch {
      setError('فشل في تحميل الاقتراحات');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const act = async (id, kind) => {
    setBusy(id);
    try {
      if (kind === 'approve') await learningService.approveSuggestion(id);
      else await learningService.rejectSuggestion(id);
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
    } catch {
      alert(kind === 'approve' ? 'فشل في اعتماد الاقتراح' : 'فشل في رفض الاقتراح');
    } finally { setBusy(null); }
  };

  const shown = useMemo(() => suggestions.slice(0, visible), [suggestions, visible]);

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', height: '60vh' }}><CircularProgress sx={{ color: T.brand }} /></Box>;
  }

  return (
    <Box>
      <PageHeader
        title="مركز التعلم الذاتي"
        subtitle="راجع الإجابات التي قدّمها موظفوك واعتمد الجيّد منها ليجيب عنها البوت تلقائياً"
        onRefresh={fetch}
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}

      {suggestions.length > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
          <Chip icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />} label={`${suggestions.length} اقتراح بانتظار المراجعة`}
            sx={{ bgcolor: '#e6f6fe', color: '#0369a1', fontWeight: 800, py: 2, px: 0.5 }} />
          <Typography sx={{ color: T.faint, fontSize: '0.8rem' }}>
            كل اعتماد يضيف المعرفة لقاعدة البوت ويقلّل التحويل للموظفين
          </Typography>
        </Stack>
      )}

      {suggestions.length === 0 ? (
        <Card elevation={0} sx={{ py: 8, textAlign: 'center' }}>
          <Box sx={{ width: 68, height: 68, borderRadius: '50%', bgcolor: '#f4f7fb', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2, color: T.faint }}>
            <SchoolRoundedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, color: T.muted }}>لا توجد اقتراحات جديدة حالياً</Typography>
          <Typography sx={{ color: T.faint, fontSize: '0.85rem', mt: 0.5 }}>
            عند رد موظفيك على العملاء من لوحة المحادثات، ستظهر الاقتراحات هنا تلقائياً.
          </Typography>
        </Card>
      ) : (
        <>
          <AnimatePresence mode="popLayout">
            {shown.map((s) => {
              const { q, a } = parseQA(s.suggested_value);
              return (
                <Card key={s.id} component={motion.div} layout
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
                  elevation={0} sx={{ mb: 2, overflow: 'hidden' }}>
                  <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Chip label="اقتراح معرفة" size="small" sx={{ bgcolor: '#efecfe', color: '#5b45d6', fontWeight: 700 }} />
                      {s.reason && <Typography sx={{ fontSize: '0.72rem', color: T.faint }}>{s.reason}</Typography>}
                    </Stack>

                    <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, p: { xs: 1.5, md: 2 }, border: `1px solid ${T.border}` }}>
                      {q && <ChatLine who="سؤال العميل" text={q} icon={<PersonRoundedIcon sx={{ fontSize: 17 }} />} bg="#ffffff" fg={T.muted} align="start" />}
                      <ChatLine who="رد الموظف" text={a} icon={<SupportAgentRoundedIcon sx={{ fontSize: 17 }} />} bg="#e6f6fe" fg="#0369a1" align="end" />
                    </Box>

                    <Divider sx={{ my: 1.75 }} />
                    <Stack direction="row" justifyContent="flex-end" spacing={1.25}>
                      <Button variant="text" color="inherit" startIcon={<CloseRoundedIcon />} disabled={busy === s.id}
                        onClick={() => act(s.id, 'reject')}
                        sx={{ color: T.muted, '& .MuiButton-startIcon': { ml: 0.5, mr: -0.25 }, '&:hover': { bgcolor: '#fff1f2', color: '#e11d48' } }}>
                        رفض
                      </Button>
                      <Button variant="contained" startIcon={busy === s.id ? <CircularProgress size={16} color="inherit" /> : <CheckRoundedIcon />}
                        disabled={busy === s.id} onClick={() => act(s.id, 'approve')}
                        sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, '& .MuiButton-startIcon': { ml: 0.5, mr: -0.25 } }}>
                        اعتماد وإضافة للمعرفة
                      </Button>
                    </Stack>
                  </Box>
                </Card>
              );
            })}
          </AnimatePresence>

          {visible < suggestions.length && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button variant="outlined" onClick={() => setVisible((v) => v + PAGE)}>
                عرض المزيد ({suggestions.length - visible} متبقٍّ)
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
